/**
 * Texts (`en`) for reprinting lot labels (OBR-178): namespaces `backoffice.lotLabelReprint` (BackOffice dialog and history) and
 * `planta.stockLabels` (the App's Stock screen). Own slice: `en.ts` is not touched. The three languages must carry the same keys
 * (i18n.test.ts); no literal text in the views.
 */

export const reprintEn = {
  'backoffice.lotLabelReprint': {
    // ── Action and dialog ─────────────────────────────────────────────────────────────
    action: 'Reprint label',
    actionNoLot: 'No lot: cannot be labelled',
    dialogTitle: 'Reprint the lot label',
    dialogBody:
      'A PDF is generated with the same lot, the same expiry and the same QR as the original label. It does not change stock or create any new lot: only a record is kept (who, when, how many copies and why).',
    noLot:
      'This stock has no lot, so it cannot be labelled. To give it a lot, regularise it from «To regularise».',
    fieldArticle: 'Article',
    fieldLot: 'Lot',
    fieldExpiry: 'Expiry',
    fieldPlace: 'Place',
    fieldQuantity: 'Stock',
    noPlace: 'No place',
    noExpiry: 'No expiry',
    copiesLabel: 'Copies',
    copiesHint: 'From 1 to 99 copies.',
    copiesInvalid: 'Enter a number of copies between 1 and 99.',
    reasonLabel: 'Reason',
    reasonPlaceholder: 'Choose a reason',
    reasonRequired: 'Choose why you are reprinting it.',
    reason_not_labelled: 'It was never labelled',
    reason_damaged: 'Damaged label',
    reason_other: 'Other',
    noteLabel: 'Note',
    noteOptionalHint: 'Optional.',
    noteRequiredHint: 'Required when the reason is «Other».',
    noteRequired: 'Write the reason for the reprint.',
    submit: 'Record and generate the PDF',
    submitting: 'Generating…',
    retryRecord: 'Record again',
    retryDownload: 'Download the PDF again',
    downloadAgain: 'Download the PDF once more',
    close: 'Close',
    cancel: 'Cancel',

    // ── Result ────────────────────────────────────────────────────────────────────────
    doneTitle: 'Label reprinted',
    doneBody_one: 'The reprint has been recorded and the PDF with {{count}} copy has been downloaded.',
    doneBody_other: 'The reprint has been recorded and the PDF with {{count}} copies has been downloaded.',
    errPdf:
      'The label PDF could not be generated. Nothing has been recorded and no label has been downloaded: try again.',
    errRecordAfterPdf:
      'The PDF is ready, but the reprint could NOT be recorded, so no label has been downloaded. Try again: it will be recorded only once.',
    errDownload:
      'The reprint has been recorded, but the browser could not download the PDF. Try again: it will not be recorded a second time.',

    // ── Lot history ───────────────────────────────────────────────────────────────────
    historyTitle: 'Reprints of this lot',
    historyShow: 'Show reprints',
    historyHide: 'Hide reprints',
    historyLoading: 'Loading reprints…',
    historyEmpty: 'This lot has never been reprinted.',
    historyError: 'The reprint history could not be loaded.',
    historyRetry: 'Try again',
    historyCopies_one: '{{count}} copy',
    historyCopies_other: '{{count}} copies',
    historyBy: 'By {{name}}',
    historyByUnknown: 'By a member of the workshop',
    historyExpiry: 'Expiry on the label: {{date}}',
    historyNoExpiry: 'No expiry on the label',
    historyOrigin_backoffice: 'From the BackOffice',
    historyOrigin_app: 'From the App',
    historyPlace: 'Place: {{place}}',
    historyNoPlace: 'No place',
    historyMore: 'Showing the {{count}} most recent reprints.',

    errQr:
      'The label’s QR code could not be generated, and a reprint is never made without the same QR. Nothing has been recorded and no label has been downloaded: try again.',
    errQrNoRoom:
      'The label format has no room for the QR (too much text for the label size). Nothing has been recorded and no label has been downloaded: check the format in Settings.',
    noticeExpiryChanged:
      'The lot’s expiry has changed since the screen was loaded: the label carries the current one ({{date}}).',
    noticeExpiryRemoved:
      'The lot no longer has an expiry since the screen was loaded: the label carries none.',
    formatLoading: 'Loading the label format…',
    formatWarning:
      'The label format from Settings could not be loaded: the reprint will use the simple format.',
    formatNone:
      'Settings has no «internal lot» format: the reprint will use the simple format.',
    logoWarning: 'The workshop logo could not be loaded: the label will come out without it.',
    historyLotExpiryWas: 'the lot had {{date}}',
    historyLotNoExpiryWas: 'the lot had no expiry',

    // ── RPC errors (never raw) ────────────────────────────────────────────────────────
    errForbidden: 'You are not allowed to record reprints in this workshop.',
    errNoteTooLong: 'The note is too long: 500 characters at most.',
    errBadCopies: 'The number of copies must be between 1 and 99.',
    errBadReason: 'Choose the reason for the reprint.',
    errNoteRequired: 'When the reason is «Other» a note is required.',
    errLotRequired: 'This stock has no lot: it cannot be labelled.',
    errUnknownLot: 'We cannot find this lot in the workshop. Reload Stock.',
    errBadPlace: 'The place of this stock no longer belongs to this workshop. Reload Stock.',
    errGeneric: 'The reprint could not be recorded. Try again.',
  },

  'planta.stockLabels': {
    // ── Entry (Reception home) and screen ─────────────────────────────────────────────
    open: 'Reprint a lot label',
    title: 'Stock',
    subtitle: 'Find a lot to reprint its label.',
    searchLabel: 'Find a lot',
    searchPlaceholder: 'Article, lot or location',
    searchClear: 'Clear the search',
    emptyNoData: 'There are no lots on this device yet. Connect it to the network so they download.',
    emptyNoMatch: 'No lot matches «{{query}}».',
    emptyWeb: 'This screen only works in the planta App, not in the web version.',
    resultsCount_one: '{{count}} lot',
    resultsCount_other: '{{count}} lots',
    resultsLimited: 'Showing the first {{count}}. Narrow the search to see more.',
    truncated:
      'The list is limited to the first {{count}} lots. Narrow the search or check Stock in the BackOffice.',

    refresh: 'Refresh the list',
    refreshing: 'Refreshing the list…',
    refreshFailed: 'The list could not be refreshed. The last copy on the device is shown.',
    refreshOffline: 'No connection: the last copy on the device is shown.',

    // ── One lot (lot + place line) ────────────────────────────────────────────────────
    lotLine: 'Lot {{lot}}',
    expiresOn: 'Expires {{date}}',
    noExpiry: 'No expiry',
    placeLine: 'Place: {{place}}',
    noPlace: 'No place',
    quantityLine: 'Stock: {{quantity}}',
    chooseLot: 'Reprint',

    // ── Reprint ───────────────────────────────────────────────────────────────────────
    printTitle: 'Reprint the label',
    reasonTitle: 'Why are you reprinting it?',
    reason_not_labelled: 'It was never labelled',
    reason_damaged: 'Damaged label',
    reason_other: 'Other',
    reasonRequired: 'Choose a reason to be able to print.',
    noteLabel: 'Explain the reason',
    notePlaceholder: 'Write it here',
    noteRequired: 'Write the reason to be able to print.',
    printCopies_one: 'Print {{count}} label',
    printCopies_other: 'Print {{count}} labels',
    printing: 'Printing…',
    back: 'Back',
    printedTo:
      'Label sent to {{printer}}. The reprint is recorded: if there is no connection, it will be sent when it comes back.',
    printedNotRecorded:
      'Label sent to {{printer}}, but the reprint record could not be saved on this device. Tell a supervisor.',
    printLeftTitle: 'Nothing was printed, so nothing was recorded.',
  },
} as const;
